// frontend/src/hooks/useColumnAggregations.ts
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AggColumn, AggFn, AggResult, ColumnAggState } from '@/types'
import { computeAggregations } from '@/utils/columnAggregations'

const PREFIX = 'agg.'

const AGG_FNS: AggFn[] = ['sum', 'avg', 'min', 'max', 'count', 'countDistinct']

function defaultsFrom<T>(columns: AggColumn<T>[]): ColumnAggState {
  const out: ColumnAggState = {}
  for (const c of columns) if (c.defaultAgg) out[c.key] = c.defaultAgg
  return out
}

/**
 * Lee la configuración guardada. Descarta claves de columnas que ya no existen
 * y funciones desconocidas, para que un renombrado no deje basura acumulada ni
 * rompa el render.
 */
function loadState<T>(storageKey: string, columns: AggColumn<T>[]): ColumnAggState {
  try {
    const raw = localStorage.getItem(PREFIX + storageKey)
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const known = new Set(columns.map(c => c.key))
      const out: ColumnAggState = {}
      for (const [k, v] of Object.entries(parsed)) {
        if (known.has(k) && AGG_FNS.includes(v as AggFn)) out[k] = v as AggFn
      }
      return out
    }
  } catch { /* sin localStorage → se cae a los valores por defecto */ }
  return defaultsFrom(columns)
}

interface Options<T> {
  /** Sufijo de la clave de localStorage. Debe ser único por tabla. */
  storageKey: string
  /** Dataset COMPLETO ya filtrado — nunca la página visible. */
  rows: T[]
  /**
   * Lista completa de columnas, incluidas las ocultas: si sólo se pasaran las
   * visibles, ocultar una columna borraría su total guardado.
   * Debe ser estable entre renders (constante de módulo o `useMemo`).
   */
  columns: AggColumn<T>[]
}

interface Result {
  aggState: ColumnAggState
  /** `null` desactiva el total de esa columna. */
  setAgg: (key: string, fn: AggFn | null) => void
  results: Record<string, AggResult>
  activeCount: number
}

/**
 * Totales de columna de una tabla: qué columna agrega qué función, el cálculo
 * sobre el dataset filtrado y la persistencia de la elección del usuario.
 */
export function useColumnAggregations<T>({ storageKey, rows, columns }: Options<T>): Result {
  const [aggState, setAggState] = useState<ColumnAggState>(() => loadState(storageKey, columns))

  useEffect(() => {
    try { localStorage.setItem(PREFIX + storageKey, JSON.stringify(aggState)) } catch { /* ignore */ }
  }, [storageKey, aggState])

  const setAgg = useCallback((key: string, fn: AggFn | null) => {
    setAggState(prev => {
      if (fn === null) {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      }
      if (prev[key] === fn) return prev
      return { ...prev, [key]: fn }
    })
  }, [])

  const results = useMemo(
    () => computeAggregations(rows, columns, aggState),
    [rows, columns, aggState],
  )

  return { aggState, setAgg, results, activeCount: Object.keys(aggState).length }
}
